import { useMemo } from 'react';
import { useTable } from 'react-table';
import { Image, Text } from '@ui/atoms';

const FdComparisonTable = ({
    fdCompareContent,
    isMobile,
    data,
    isExpanded,
}) => {
    const mobileColumns = useMemo(
        () => [
            {
                Header: 'Bank',
                accessor: 'bank',
                Cell: ({ row }) => (
                    <div className="flex items-center gap-2">
                        <Image
                            src={`/images/products/banks/${row.original.logo}.svg`}
                            alt={row.original.bank}
                            className="w-6 h-6"
                        />
                        <Text
                            content={row.original.bank}
                            className="p5-medium text-primary-500"
                        />
                    </div>
                ),
                maxWidth: 200,
            },
            {
                Header: 'General citizens (1 year)',
                accessor: 'year1',
                maxWidth: 100,
            },
            {
                Header: 'General citizens (3 years)',
                accessor: 'year3',
                maxWidth: 100,
            },
        ],
        []
    );
    const desktopData = useMemo(
        () => [
            {
                duration: 'General citizens \n (1 year)',
                ...fdCompareContent.reduce(
                    (acc, bank) => ({ ...acc, [bank.bank]: bank.year1 }),
                    {}
                ),
            },
            {
                duration: 'General citizens (3 years)',
                ...fdCompareContent.reduce(
                    (acc, bank) => ({ ...acc, [bank.bank]: bank.year3 }),
                    {}
                ),
            },
        ],
        []
    );
    const desktopColumns = useMemo(
        () => [
            {
                Header: 'Bank',
                accessor: 'duration',
                fixed: 'left',
                maxWidth: 160,
                width: 160,
            },
            ...data.map((bank) => ({
                Header: (
                    <div className="flex items-center gap-2">
                        <Image
                            src={`/images/products/banks/${bank.logo}.svg`}
                            alt={bank.bank}
                            className="w-8 h-8"
                        />
                        <Text
                            content={bank.bank}
                            className="p4-medium text-primary-500"
                            htmlTag="span"
                        />
                    </div>
                ),
                accessor: bank.bank,
                maxWidth: 182,
                width: 182,
            })),
        ],
        []
    );

    const visibleData = isMobile && !isExpanded ? data.slice(0, 4) : data;
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({
            columns: isMobile ? mobileColumns : desktopColumns,
            data: isMobile ? visibleData : desktopData,
        });

    return (
        <div
            className={`relative  w-full border rounded-xl mt-6 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200  thin-scrollbar comparison-table ${
                isMobile ? 'h-auto overflow-hidden' : 'overflow-x-auto'
            }`}
        >
            <table
                {...getTableProps()}
                className="w-full border-collapse min-w-max"
            >
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr
                            {...headerGroup.getHeaderGroupProps()}
                            className="bg-gray-100"
                        >
                            {headerGroup.headers.map((column) => (
                                <th
                                    {...column.getHeaderProps()}
                                    className={`px-3 py-3 text-left border-b border-r last:border-r-0 border-gray-200 font-semibold ${
                                        !isMobile
                                            ? column.fixed === 'left'
                                                ? 'sticky left-0 bg-gray-50 shadow-fd-table-shadow p4-regular'
                                                : 'bg-basicWhite p4-medium text-gray-700 break-keep'
                                            : 'p6-medium'
                                    }`}
                                >
                                    {column.render('Header')}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row) => {
                        prepareRow(row);
                        return (
                            <tr
                                {...row.getRowProps()}
                                className="border-b odd:bg-basicWhite even:bg-gray-50 last:border-b md:border-b-0 md:odd:border-b"
                            >
                                {row.cells.map((cell) => (
                                    <td
                                        {...cell.getCellProps({
                                            style: isMobile
                                                ? {
                                                      minWidth:
                                                          cell.column.minWidth,
                                                      width: cell.column.width,
                                                      maxWidth:
                                                          cell.column.maxWidth,
                                                  }
                                                : {
                                                      width: cell.column.width,
                                                  },
                                        })}
                                        className={`px-3 py-3 border-r last:border-r-0 border-gray-200 text-left ${
                                            !isMobile
                                                ? cell.column.fixed === 'left'
                                                    ? 'sticky left-0 bg-gray-50 shadow-fd-table-shadow p4-regular '
                                                    : 'bg-basicWhite p4-medium text-gray-700 '
                                                : 'p6-medium'
                                        }`}
                                    >
                                        {cell.render('Cell')}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default FdComparisonTable;
